import { createSelector } from 'reselect';
import { getFormValues } from "redux-form";
import {isPrequalification} from '../../utils/helpers';

const selectMerchant = state => state.leaseForm;
const selectFormValues = state => getFormValues('form')(state);

const formValuesSelector = () => createSelector(
  selectFormValues,
  (values) => values
);

const paymentPeriodSelector = () => createSelector(
  selectFormValues,
  (values) => values && values.paymentPeriod
);

const activeStepSelector = () => createSelector(
  selectMerchant,
  (merchant) => merchant.activeStep
);

const allSelectedOffersSelector = () => createSelector(
  selectMerchant,
  (merchant) => merchant.allSelectedOffers
);

const multipleIframeFlagSelector = () => createSelector(
  selectMerchant,
  (merchant) => merchant.applicationRequest.multipleIframeEnabled
);

const acimaConfigSelector = () => createSelector(
  selectMerchant,
  (merchant) => merchant.applicationRequest.acimaConfig
);

const appRequestSelector = () => createSelector(
  selectMerchant,
  (merchant) => merchant.applicationRequest
);

const userInfoSelector = () => createSelector(
  selectMerchant,
  (merchant) => merchant.applicationRequest.personalInfo
);

const trendingsVisitID = () => createSelector(
  selectMerchant,
  (merchant) => merchant.applicationRequest.visitId
);

const subscribingDataSelector = () => createSelector(
  selectMerchant,
  (merchant) => {
    let total_price = 0;
    const line_items = merchant.applicationRequest.items ? merchant.applicationRequest.items.map(item => {
      total_price += item.price * item.quantity;
      return {
        price: item.price,
        quantity: item.quantity,
        category: item.category,
        brand: item.brand,
        model: item.model,
        image: item.image,
        id: item.id,
        sku: item.sku,
        taxAmount: item.taxAmount,
        specifications: item.specifications,
        recommendation_type: item.recommendation_type,
        productType: item.productType || item.product_type,
        product: {
          title: `${item.brand} ${item.model}`,
          sku: item.sku
        }
      }
    }) : []
    return {
      customer: {
        phone: merchant.applicationRequest.personalInfo.phone
      },
      line_items: line_items,
      total_price: total_price,
      order_id: merchant.applicationRequest.orderId
    }
  }
);

const initApplicationResponseSelector = () => createSelector(
  selectMerchant,
  (merchant) => merchant.applicationResponse
);

const approvedApplicationsSelector = () => createSelector(
  selectMerchant,
  (merchant) => merchant.applicationResponse.offers.filter(o => o.approve_status === 'approved')
);

const availableApplicationsSelector = () => createSelector(
  selectMerchant,
  (merchant) => {
    const offers = merchant.applicationResponse?.offers;
    if (!offers) {
      return [];
    }
    return offers.filter(o => o.approve_status === 'approved' && o.available);
  }
);

const notAvailableApplicationsSelector = () => createSelector(
  selectMerchant,
  (merchant) => merchant.applicationResponse.offers.filter(o => o.approve_status === 'approved' && !o.available)
);

// Special providers that should be filtered by cart amount coverage
const SPECIAL_PROVIDERS = ['kornerstone', 'progressive'];

// Progressive and the Uown extra-info offer are held back while a regular
// lender covers the cart, unless the lender got the bank account with the
// application (bank_sent): then the offer is shown like a regular one.
const isSpecialOffer = (offer) => {
  if (offer.bank_sent) return false;
  return SPECIAL_PROVIDERS.includes(offer.provider) || (offer.provider === 'uown' && offer.bank_data_is_required);
};

const availableApplicationsCoveringCartSelector = () => createSelector(
  selectMerchant,
  (merchant) => {
    const offers = merchant.applicationResponse?.offers;
    const cartAmountWithTax = merchant.applicationResponse?.amount;
    if (!offers || !cartAmountWithTax) {
      return [];
    }
    
    const getCartAmountWithoutTax = (offer) => {
      if (offer.cart_total !== undefined) {
        return offer.cart_total;
      }
      const fullTaxes = parseFloat(offer.full_taxes || 0);
      return cartAmountWithTax - fullTaxes;
    };
    
    const regularProvidersFullyCovering = offers.filter(o => {
      if (o.approve_status !== 'approved' || !o.available) return false;
      if (isSpecialOffer(o)) return false;

      const cartAmountWithoutTax = getCartAmountWithoutTax(o);
      return o.approved_amount >= cartAmountWithoutTax;
    });

    const hasRegularFullCoverage = regularProvidersFullyCovering.length > 0;

    return offers.filter(o => {
      if (o.approve_status !== 'approved' || !o.available) return false;
      if (!isSpecialOffer(o)) return true;

      const cartAmountWithoutTax = getCartAmountWithoutTax(o);

      if (o.provider === 'uown' && o.bank_data_is_required) {
        if (o.approved_amount >= cartAmountWithoutTax && !hasRegularFullCoverage) {
          return true;
        }
        return !hasRegularFullCoverage && cartAmountWithoutTax <= 2000;
      }
      
      return o.approved_amount >= cartAmountWithoutTax && !hasRegularFullCoverage;
    });
  }
);

const applicationsNotCoveringCartSelector = () => createSelector(
  selectMerchant,
  (merchant) => {
    const offers = merchant.applicationResponse?.offers;
    const cartAmountWithTax = merchant.applicationResponse?.amount;
    if (!offers || !cartAmountWithTax) {
      return [];
    }
    
    return offers.filter(o => {
      if (o.approve_status !== 'approved') return false;
      
      if (isSpecialOffer(o) && o.available) {
        return false; // Never show in bottom section
      }

      return !o.available;
    });
  }
);

const recommendationsByItemSelector = () => createSelector(
  selectMerchant,
  (merchant) => merchant.recommendationsByItem || {}
);

const recommendationsByItemLoadingSelector = () => createSelector(
  selectMerchant,
  (merchant) => merchant.recommendationsByItemLoading || {}
);

const additionalsRequiredApplicationsSelector = () => createSelector(
  selectMerchant,
  (merchant) => merchant.applicationResponse.offers.filter(o => o.approve_status === 'need_additionals')
);

const additionalsProvidedSelector = () => createSelector(
  selectMerchant,
  (merchant) => merchant.applicationResponse.additionals_provided
);

const cartAmountSelector = () => createSelector(
  selectMerchant,
  (merchant) => merchant.applicationResponse.amount
);

const selectedOfferSelector = () => createSelector(
  selectMerchant,
  (merchant) => merchant.selectedOffer
);

// const selectedOfferAcceptedSelector = () => createSelector(
//   selectMerchant,
//   (merchant) => merchant.selectedOffer.contract ? true : false
// );

const applicationHasContractSelector = () => createSelector(
  selectMerchant,
  (merchant) => merchant.selectedOffer && (merchant.selectedOffer.contract || merchant.selectedOffer.contractUrl) ? true : false
);

const loadingSelector = () => createSelector(
  selectMerchant,
  (merchant) => merchant.loading,
);

const closingSelector = () => createSelector(
  selectMerchant,
  (merchant) => merchant.closing,
);

const selectedOfferSignedSelector = () => createSelector(
  selectMerchant,
  (merchant) => merchant.selectedOfferSigned,
);

const step1Selector = () => createSelector(
  selectMerchant,
  (merchant) => merchant.step1,
);


const activeStepDataSelector = () => createSelector(
  selectMerchant,
  (merchant) => merchant.activeStepData
);

const metrixSessionIdSelector = () => createSelector(
  selectMerchant,
  (merchant) => merchant.metrixSessionId
);

const threatMetrixPayTomorrowSessionIdSelector = () => createSelector(
  selectMerchant,
  (merchant) => merchant.threatMetrixPayTomorrowSessionId
);

const snapSessionIdSelector = () => createSelector(
  selectMerchant,
  (merchant) => merchant.snapSessionId
)

const uownSessionIdSelector = () => createSelector(
  selectMerchant,
  (merchant) => merchant.uownSessionId
)

const blackboxIdSelector = () => createSelector(
  selectMerchant,
  (merchant) => merchant.blackboxId
);

const koalafiNeuroIdSelector = () => createSelector(
  selectMerchant,
  (merchant) => merchant.koalafiNeuroId
);

export const currentMerchant  = () => createSelector(
  selectMerchant,
  (merchant) => merchant.merchant
)

export const activeProviders  = () => createSelector(
  selectMerchant,
  (merchant) => merchant.activeProviders
)

const affiliatedProviderSelector  = () => createSelector(
  selectMerchant,
  (merchant) => merchant.applicationRequest.provider_name
)

const prequalificationSelector = () => createSelector(
  selectMerchant,
  (merchant) => isPrequalification(merchant.applicationRequest.prequalification)
)

const b2bSelector = () => createSelector(
  selectMerchant,
  (merchant) => merchant.applicationRequest.b2b === true
)

const maxApproveSelector = () => createSelector(
  selectMerchant,
  (merchant) => merchant.maxApprove
);

const affAgreements = () => createSelector(
  selectMerchant,
  (merchant) => merchant.affAgreements
);

const cartLoadingSelector = () => createSelector(
  selectMerchant,
  (merchant) => merchant.cartModalLoading,
);

const cartModalOpenSelector = () => createSelector(
  selectMerchant,
  (merchant) => merchant.cartModalOpen
);

const cartErrorSelector = () => createSelector(
  selectMerchant,
  (merchant) => merchant.cartError
);

const prequalDataSelector = () => createSelector(
  selectMerchant,
  (merchant) => merchant.prequalData
);

const discountDataSelector = () => createSelector(
  selectMerchant,
  (merchant) => {
    return {
      amount: merchant.discount,
      preDiscountTotal: merchant.preDiscountTotal,
    }
  }
);

export {
  selectMerchant,
  activeStepSelector,
  initApplicationResponseSelector,
  approvedApplicationsSelector,
  availableApplicationsSelector,
  availableApplicationsCoveringCartSelector,
  applicationsNotCoveringCartSelector,
  notAvailableApplicationsSelector,
  additionalsRequiredApplicationsSelector,
  additionalsProvidedSelector,
  cartAmountSelector,
  userInfoSelector,
  selectedOfferSelector,
  // selectedOfferAcceptedSelector,
  applicationHasContractSelector,
  loadingSelector,
  closingSelector,
  formValuesSelector,
  paymentPeriodSelector,
  selectedOfferSignedSelector,
  step1Selector,
  activeStepDataSelector,
  metrixSessionIdSelector,
  threatMetrixPayTomorrowSessionIdSelector,
  snapSessionIdSelector,
  uownSessionIdSelector,
  blackboxIdSelector,
  koalafiNeuroIdSelector,
  appRequestSelector,
  subscribingDataSelector,
  affiliatedProviderSelector,
  trendingsVisitID,
  prequalificationSelector,
  b2bSelector,
  maxApproveSelector,
  affAgreements,
  cartLoadingSelector,
  cartModalOpenSelector,
  cartErrorSelector,
  discountDataSelector,
  allSelectedOffersSelector,
  multipleIframeFlagSelector,
  prequalDataSelector,
  recommendationsByItemSelector,
  recommendationsByItemLoadingSelector,
  acimaConfigSelector
};
